"use client"

import { Radio, RadioGroup } from "@headlessui/react"
import Image from "next/image"

import { tv, type VariantProps } from "@/lib/utils/tv"

const radioCards = tv({
  slots: {
    group: "flex flex-col gap-250",
    option: "group font-text text-body text-fg inline-flex cursor-pointer items-center gap-250 data-disabled:cursor-not-allowed data-disabled:opacity-50",
  },
  variants: {
    compact: {
      true: {
        group: "border-fg-secondary/10 rounded-7-5x gap-0 border px-500 py-300",
        option: "text-little text-fg-heading focus-visible:ring-brand h-8.25 justify-between gap-300 rounded-2x font-medium focus-visible:ring-2 focus-visible:outline-none",
      },
    },
    divided: {
      true: {
        group: "bg-surface border-line-sand divide-line-sand rounded-10x gap-0 divide-y overflow-hidden border",
        option:
          "text-body-lg focus-visible:ring-brand min-h-15 w-full justify-between gap-300 px-400 py-300 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
      },
    },
    choice: {
      true: {
        group: "gap-200",
        option:
          "text-body-lg tracking-snug min-h-18 w-full justify-between gap-300 rounded-8x border border-line-field bg-surface px-400 py-300 font-medium data-checked:border-brand data-checked:bg-surface-baby-blue focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none",
      },
    },
  },
})

interface RadioOption<T extends string> {
  value: T
  label: string
  disabled?: boolean
}

interface RadioCardsProps<T extends string> extends VariantProps<typeof radioCards> {
  value?: T
  onChange: (value: T) => void
  options: RadioOption<T>[]
  name?: string
  label?: string
  onReselect?: (value: T) => void
  /** Commits an option on click, Enter or Space. Arrow keys then only move focus and still report through onChange. On click it fires alongside onChange and onReselect. */
  onChoose?: (value: T) => void
  /** Focuses the selected option on mount (the first one when none is selected). */
  autoFocusSelected?: boolean
}

export const RadioCards = <T extends string>({ value, onChange, options, name, label, compact, divided, choice, onReselect, onChoose, autoFocusSelected }: RadioCardsProps<T>) => {
  const styles = radioCards({ compact, divided, choice })
  const select = (next: T | null) => {
    if (next !== null) onChange(next)
  }
  return (
    <RadioGroup value={value ?? null} onChange={select} name={name} aria-label={label} className={styles.group()}>
      {options.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          autoFocus={!!autoFocusSelected && option.value === (value ?? options[0]?.value)}
          onClick={() => {
            if (option.disabled) return
            if (option.value === value) onReselect?.(option.value)
            onChoose?.(option.value)
          }}
          onKeyDown={(event) => {
            if (onChoose && !option.disabled && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault()
              event.stopPropagation()
              onChoose(option.value)
              return
            }
            if (!onReselect || option.disabled || (event.key !== "Enter" && (event.key !== " " || option.value !== value))) return
            event.preventDefault()
            event.stopPropagation()
            if (option.value === value) onReselect(option.value)
            else onChange(option.value)
          }}
          className={styles.option()}
        >
          {({ checked }) =>
            compact ? (
              <>
                {option.label}
                <Image src={checked ? "/images/icons/radio-selected.svg" : "/images/icons/radio-unselected.svg"} alt="" width={16} height={16} className="shrink-0" />
              </>
            ) : divided || choice ? (
              <>
                {option.label}
                <span className="border-fg-secondary bg-surface group-data-checked:border-brand flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors">
                  <span className="bg-brand size-2.5 scale-0 rounded-full transition-transform group-data-checked:scale-100" />
                </span>
              </>
            ) : (
              <>
                <span className="border-line bg-surface group-data-checked:border-brand border-field flex size-5.5 items-center justify-center rounded-full transition-colors">
                  <span className="bg-brand size-2.5 scale-0 rounded-full transition-transform group-data-checked:scale-100" />
                </span>
                {option.label}
              </>
            )
          }
        </Radio>
      ))}
    </RadioGroup>
  )
}
